import React, { useState } from "react";
import { Shield, CheckCircle2, ChevronDown } from "lucide-react";

const styles = `
.securityAuthPage {
  background: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #1a1a18;
}
.securityAuthGrid {
  width: 100%;
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 20px;
  align-items: start;
}
.securityAuthLeftCol, .securityAuthRightCol {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.securityAuthCard {
  background: #ffffff;
  border: 1px solid #ececea;
  border-radius: 14px;
  padding: 22px 24px;
}
.securityAuthRowBetween {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
}
.securityAuthSectionTitle { font-size: 16px; font-weight: 500; color: #1a1a18; padding-bottom: 4px; }
.securityAuthSubtitle { font-size: 14px; color: #8a8a86; margin-top: 4px; line-height: 1.5; max-width: 360px; }
.securityAuthRowTitle { font-size: 16px; font-weight: 500; color: #1a1a18; }
.securityAuthDots { letter-spacing: 3px; font-size: 14px; color: #8a8a86; margin-top: 4px; }
.securityAuthDivider { height: 1px; background: #efefec; }
.securityAuthButton {
  border: 1px solid #e2e2de; background: #ffffff; border-radius: 8px;
  padding: 8px 14px; font-size: 13px; font-weight: 500; color: #1a1a18;
  cursor: pointer; white-space: nowrap;
}
.securityAuthButtonDanger { color: #dc2626; border-color: #f3d0d0; }
.securityAuthSelectWrap { position: relative; display: flex; align-items: center; }
.securityAuthSelect {
  appearance: none; border: 1px solid #e2e2de; background: #ffffff;
  border-radius: 8px; padding: 8px 30px 8px 12px; font-size: 13px;
  font-weight: 500; color: #1a1a18; cursor: pointer;
}
.securityAuthSelectChevron { position: absolute; right: 10px; pointer-events: none; color: #8a8a86; }
.securityAuthPill { border: none; padding: 6px 14px; font-size: 12px; font-weight: 600; cursor: pointer; }
.securityAuthSignOutRow { display: flex; justify-content: flex-end; padding-top: 16px; margin-top: 4px; border-top: 1px solid #efefec; }
.securityAuthToggleRow { display: flex; align-items: center; justify-content: space-between; padding: 12px 0; }
.securityAuthToggleLabel { font-size: 14px; font-weight: 500; color: #1a1a18; }
.securityAuthToggleTrack {
  width: 40px; height: 22px; border-radius: 20px; border: none;
  position: relative; cursor: pointer; transition: background 0.15s ease; padding: 0;
}
.securityAuthToggleTrack.securityAuthOn { background: #4f46e5; }
.securityAuthToggleTrack.securityAuthOff { background: #d8d7d0; }
.securityAuthToggleThumb {
  position: absolute; top: 2px; left: 2px; width: 18px; height: 18px;
  border-radius: 50%; background: #ffffff; transition: transform 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.securityAuthToggleThumb.securityAuthOn { transform: translateX(18px); }
.securityAuthPill.securityAuthActive { background: #dcfce7; color: #15803d; }
.securityAuthPill.securityAuthInactive { background: #f1f1ef; color: #6b6b68; }
.securityAuthScoreRow { display: flex; align-items: center; gap: 14px; margin-top: 16px; }
.securityAuthShieldWrap {
  width: 57px; height: 57px; border-radius: 50%; background: #eafaf0;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.securityAuthScoreNum { font-size: 28px; font-weight: 700; color: #1a1a18; }
.securityAuthScoreDenom { font-size: 16px; color: #8a8a86; }
.securityAuthScoreLabel { font-size: 14px; font-weight: 600; color: #16a34a; margin-top: 2px; }
.securityAuthProgressTrack { height: 6px; background: #eeede8; border-radius: 4px; margin-top: 16px; overflow: hidden; }
.securityAuthProgressFill { height: 100%; background: #22c55e; border-radius: 4px; width: 96%; }
.securityAuthCheckRow { display: flex; align-items: center; gap: 8px; padding: 6px 0; }
.securityAuthCheckText { font-size: 14px; color: #3c3c39; }
.securityAuthEventRow { padding: 10px 0; border-bottom: 1px solid #f1f1ee; }
.securityAuthEventTitle { font-size: 14px; font-weight: 500; color: #1a1a18; }
.securityAuthEventDate { font-size: 14px; color: #9a9a96; margin-top: 3px; }
.securityAuthViewAllLink { display: inline-block; margin-top: 14px; font-size: 14px; font-weight: 600; color: #4f46e5; text-decoration: none; }
`;

export default function SecurityAuthenticationPage() {
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState("30 minutes");
  const [requireMfa, setRequireMfa] = useState(true);
  const [allowPasswordLogin, setAllowPasswordLogin] = useState(true);
  const [notifyNewDevice, setNotifyNewDevice] = useState(true);

  const securityChecks = ["Strong password", "MFA enabled", "Email verified", "Phone verified", "Security questions set"];
  const events = [
    { title: "New sign-in from Chrome on Windows", date: "Jun 19, 2026 10:30 AM" },
    { title: "MFA enabled", date: "Jun 18, 2026 04:15 PM" },
    { title: "Password changed", date: "May 20, 2026 11:22 AM" },
    { title: "New device trusted", date: "May 16, 2026 09:45 AM" },
  ];

  return (
    <div className="securityAuthPage">
      <style>{styles}</style>
      <div className="securityAuthGrid">
        <div className="securityAuthLeftCol">
          <div className="securityAuthCard">
            <SecurityAuthSectionHeader
              title="Multi-Factor Authentication (MFA)"
              subtitle="Add an extra layer of security to your account."
              right={<SecurityAuthStatusPill active={mfaEnabled} onClick={() => setMfaEnabled(!mfaEnabled)} />}
            />
            <SecurityAuthDivider />
            <SecurityAuthRow title="Password" subtitle="Last changed 30 days ago" dots action={<SecurityAuthButton>Change Password</SecurityAuthButton>} />
            <SecurityAuthDivider />
            <SecurityAuthRow title="Backup Codes" subtitle="Use backup codes to sign in when you lose access to your authenticator. 10 codes remaining" action={<SecurityAuthButton>Regenerate Codes</SecurityAuthButton>} />
            <SecurityAuthDivider />
            <SecurityAuthRow title="Trusted Devices" subtitle="You have 3 trusted devices." action={<SecurityAuthButton>Manage Devices</SecurityAuthButton>} />
          </div>

          <div className="securityAuthCard">
            <SecurityAuthSectionTitle>Session Management</SecurityAuthSectionTitle>
            <SecurityAuthRow
              title="Session Timeout"
              subtitle="Automatically log out after a period of inactivity."
              action={<SecurityAuthSelect value={sessionTimeout} onChange={setSessionTimeout} options={["15 minutes", "30 minutes", "1 hour", "4 hours"]} />}
            />
            <SecurityAuthDivider />
            <SecurityAuthRow title="Active Sessions" subtitle="You are currently signed in on 2 devices." action={<SecurityAuthButton>View Sessions</SecurityAuthButton>} />
          </div>

          <div className="securityAuthCard">
            <SecurityAuthSectionTitle>Security Settings</SecurityAuthSectionTitle>
            <SecurityAuthToggleRow label="Require MFA for all sign-ins" checked={requireMfa} onChange={() => setRequireMfa(!requireMfa)} />
            <SecurityAuthDivider />
            <SecurityAuthToggleRow label="Allow password login" checked={allowPasswordLogin} onChange={() => setAllowPasswordLogin(!allowPasswordLogin)} />
            <SecurityAuthDivider />
            <SecurityAuthToggleRow label="Notify on new device login" checked={notifyNewDevice} onChange={() => setNotifyNewDevice(!notifyNewDevice)} />
            <div className="securityAuthSignOutRow"><SecurityAuthButton danger>Sign Out All</SecurityAuthButton></div>
          </div>
        </div>

        <div className="securityAuthRightCol">
          <div className="securityAuthCard">
            <SecurityAuthSectionTitle>Account Security Score</SecurityAuthSectionTitle>
            <div className="securityAuthScoreRow">
              <div className="securityAuthShieldWrap"><Shield size={26} color="#16a34a" strokeWidth={2} /></div>
              <div>
                <span className="securityAuthScoreNum">96</span>
                <span className="securityAuthScoreDenom"> / 100</span>
                <div className="securityAuthScoreLabel">Very Strong</div>
              </div>
            </div>
            <div className="securityAuthProgressTrack"><div className="securityAuthProgressFill" /></div>
            <div style={{ marginTop: 20 }}>
              {securityChecks.map((c) => (
                <div key={c} className="securityAuthCheckRow">
                  <CheckCircle2 size={16} color="#16a34a" strokeWidth={2} />
                  <span className="securityAuthCheckText">{c}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="securityAuthCard">
            <SecurityAuthSectionTitle>Recent Security Events</SecurityAuthSectionTitle>
            <div style={{ marginTop: 8 }}>
              {events.map((e) => (
                <div key={`${e.title}-${e.date}`} className="securityAuthEventRow">
                  <div className="securityAuthEventTitle">{e.title}</div>
                  <div className="securityAuthEventDate">{e.date}</div>
                </div>
              ))}
            </div>
            <a href="#" className="securityAuthViewAllLink" onClick={(ev) => ev.preventDefault()}>View All Security Events &rarr;</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SecurityAuthSectionHeader({ title, subtitle, right }) {
  return (
    <div className="securityAuthRowBetween">
      <div>
        <div className="securityAuthSectionTitle">{title}</div>
        {subtitle && <div className="securityAuthSubtitle">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}
function SecurityAuthSectionTitle({ children }) { return <div className="securityAuthSectionTitle">{children}</div>; }
function SecurityAuthRow({ title, subtitle, action, dots }) {
  return (
    <div className="securityAuthRowBetween">
      <div>
        <div className="securityAuthRowTitle">{title}</div>
        {dots && <div className="securityAuthDots">&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</div>}
        {subtitle && <div className="securityAuthSubtitle">{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}
function SecurityAuthButton({ children, danger }) {
  return <button className={`securityAuthButton${danger ? " securityAuthButtonDanger" : ""}`}>{children}</button>;
}
function SecurityAuthSelect({ value, onChange, options }) {
  return (
    <div className="securityAuthSelectWrap">
      <select value={value} onChange={(e) => onChange(e.target.value)} className="securityAuthSelect">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown size={14} className="securityAuthSelectChevron" />
    </div>
  );
}
function SecurityAuthStatusPill({ active, onClick }) {
  return (
    <button onClick={onClick} className={`securityAuthPill ${active ? "securityAuthActive" : "securityAuthInactive"}`}>
      {active ? "Enabled" : "Disabled"}
    </button>
  );
}
function SecurityAuthToggleRow({ label, checked, onChange }) {
  return (
    <div className="securityAuthToggleRow">
      <span className="securityAuthToggleLabel">{label}</span>
      <button onClick={onChange} className={`securityAuthToggleTrack ${checked ? "securityAuthOn" : "securityAuthOff"}`}>
        <span className={`securityAuthToggleThumb ${checked ? "securityAuthOn" : ""}`} />
      </button>
    </div>
  );
}
function SecurityAuthDivider() { return <div className="securityAuthDivider" />; }